<template>
  <v-data-table
    :headers="_headers"
    :items="sortedNotes"
    no-data-text=""
    hide-default-footer
    disable-pagination
  >
    <template v-slot:[`item.noteText`]="{ item }">
      <v-edit-dialog
        :ref="'dialog-' + item.caseNoteId"
        :return-value="editedNoteText"
        large
        @save="editNote(item)"
      >
        <!-- Displayed Note Text -->
        {{ item.noteText }}

        <!-- Note Text Editor -->
        <template v-slot:input>
          <v-lazy>
            <v-textarea
              v-model="editedNoteText"
              label="Edit Note"
              autofocus
              rows="1"
              auto-grow
            ></v-textarea>
          </v-lazy>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:[`item.subject`]="{ item }">
      <v-menu v-model="item.menu" :close-on-content-click="false">
        <template v-slot:activator="{ on }">
          <v-chip v-on="on" small>{{ item.subjectValue }}</v-chip>
        </template>
        <v-card>
          <v-card-title>
            <case-note-subject-select
              :noteSubject="item.subjectValue"
              @setNoteSubject="setNoteSubject($event, item)"
            ></case-note-subject-select>
          </v-card-title>
        </v-card>
      </v-menu>
    </template>
    <template v-slot:[`item.tags`]="{ item }">
      <v-chip
        v-for="tag in item.tags"
        :key="tag.tagId"
        x-small
        label
        color="primary"
      >
        <v-icon x-small left> mdi-label </v-icon>{{ tag.name }}</v-chip
      >
      <v-menu v-model="item.menu2" :close-on-content-click="false">
        <template v-slot:activator="{ on }">
          <v-chip v-on="on" x-small label color="primary" class="px-2"
            ><v-icon small>mdi-plus</v-icon>/<v-icon small
              >mdi-minus</v-icon
            ></v-chip
          >
        </template>
        <v-card>
          <v-card-title>
            <case-note-tags-select
              :noteTags="item.tags"
              @setNoteTags="setNoteTags($event, item)"
            ></case-note-tags-select>
          </v-card-title>
        </v-card>
      </v-menu>
    </template>
    <template v-slot:[`item.isInternal`]="{ item }">
      <v-simple-checkbox
        :value="Boolean(item.isInternal)"
        disabled
      ></v-simple-checkbox
    ></template>
    <template v-slot:[`item.actions`]="{ item }">
      <div class="d-flex flex-nowrap">
        <v-btn v-if="canEdit(item)" small icon @click="openEditDialog(item)">
          <v-icon small>mdi-pencil</v-icon></v-btn
        ><v-btn v-if="canEdit(item)" small icon @click="deleteNote(item)">
          <v-icon small>mdi-delete</v-icon></v-btn
        >
      </div>
    </template>
  </v-data-table>
</template>

<script>
import CaseNoteSubjectSelect from "./case-note-subject-select.vue";
import CaseNoteTagsSelect from "./case-note-tags-select.vue";
export default {
  components: { CaseNoteSubjectSelect, CaseNoteTagsSelect },
  props: ["notes", "noEdit"],
  mounted() {
    // update the current time every 60 seconds for the delete button timer
    this.interval = window.setInterval(
      (() => {
        return () => {
          this.currentTime = new Date();
        };
      })(),
      60000
    );
  },
  destroyed() {
    window.clearInterval(this.interval);
  },
  data() {
    return {
      headers: [
        { text: "Note", value: "noteText" },
        { text: "Subject", value: "subject", width: "5%" },
        { text: "Tags", value: "tags", width: "3%" },
        {
          text: "Internal For Omni",
          value: "isInternal",
          hide: this.noEdit,
          width: "2%",
        },
        { text: "Created On", value: "formattedDate", width: "5%" },
        { text: "Created By", value: "name", width: "5%" },
        { text: "Actions", value: "actions", hide: this.noEdit, width: "1%" },
      ],
      interval: null,
      currentTime: new Date(),
      editedNoteText: "",
    };
  },
  computed: {
    _headers() {
      return this.headers.filter((x) => !x.hide);
    },
    sortedNotes() {
      return this.notes.slice().sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    },
    isCustomSubject() {
      return typeof this.noteSubject === "string";
    },
  },
  methods: {
    canEdit(note) {
      var userId = this.$store.getters["securityModule/getUserId"];
      // only show edit/delete button if the user is the author of the note and it's within 60 minutes of createdAt
      return (
        userId != "" &&
        userId == note.userId &&
        (this.currentTime - new Date(note.createdAt).getTime()) / 60000 <= 60
      );
    },
    async setNoteSubject(subject, note) {
      await this.updateNote({
        ...note,
        ...(!(typeof subject === "string") && {
          subjectId: subject?.subjectId,
          customSubject: "",
        }),
        ...(typeof subject === "string" && {
          subjectId: "",
          customSubject: subject,
        }),
      });
    },
    async setNoteTags(tags, note) {
      await this.updateNote({ ...note, tags: tags });
    },
    async updateNote(note) {
      this.$store.commit("uxModule/setShowLoader", true);
      let success = await this.$store.dispatch("clientModule/updateCaseNote", {
        ...note,
      });
      this.$store.commit("uxModule/setShowLoader", false);

      if (!success) {
        this.$store.commit("uxModule/setSnackbarMsg", "Error saving note");
        this.$store.commit("uxModule/setShowSnackbar", true);
      }
    },
    async deleteNote(item) {
      if (this.canEdit(item)) {
        this.$store.commit("uxModule/setShowLoader", true);
        let message = await this.$store.dispatch(
          "clientModule/deleteCaseNote",
          {
            caseNoteId: item.caseNoteId,
          }
        );
        this.$store.commit("uxModule/setShowLoader", false);
        if (message) {
          this.$store.commit("uxModule/setSnackbarMsg", message);
          this.$store.commit("uxModule/setShowSnackbar", true);
        }
      } else {
        this.$store.commit("uxModule/setSnackbarMsg", "Cannot delete note.");
        this.$store.commit("uxModule/setShowSnackbar", true);
      }
    },
    async editNote(item) {
      if (this.canEdit(item)) {
        this.$store.commit("uxModule/setShowLoader", true);
        let success = await this.$store.dispatch(
          "clientModule/updateCaseNote",
          {
            ...item,
            noteText: this.editedNoteText,
          }
        );
        this.$store.commit("uxModule/setShowLoader", false);

        if (!success) {
          this.$store.commit("uxModule/setSnackbarMsg", "Error saving note");
          this.$store.commit("uxModule/setShowSnackbar", true);
        }
      } else {
        this.$store.commit("uxModule/setSnackbarMsg", "Cannot edit note");
        this.$store.commit("uxModule/setShowSnackbar", true);
      }
    },
    openEditDialog(item) {
      this.editedNoteText = item.noteText;
      const noteTextEditDialog = this.$refs["dialog-" + item.caseNoteId];
      if (Boolean(noteTextEditDialog)) {
        noteTextEditDialog.isActive = true;
      }
    },
  },
};
</script>
<style>
table {
  border-collapse: collapse;
}
.v-small-dialog__activator {
  pointer-events: none;
}
</style>
